import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { IPromise } from 'angular'
import { $rootScope } from '../../ng-compat/imports'
import { FulfillmentOutlet } from '../react/modals/mark-as-unfulfilled-modal/Steps/types'
import { outletsStore } from '../../factories/database/outlets-store'
import { getFeatureFromCache } from '../../util/feature-cache'
import { CashFloatSettings } from '../cash-management/utils/types'

type SaleModalType =
  | 'QuoteCustomer'
  | 'Service'
  | 'Unfulfilled'
  | 'OpenRegister'
  | 'OutletsDifferWarning'
  | 'AddCustomer'
  | 'LSPayRecovery'
  | null

type SaleModalListener = () => void

interface BaseSaleModalState {
  type: SaleModalType
}

interface EmptySaleModalState extends BaseSaleModalState {
  type: null
}

interface QuoteCustomerModalState extends BaseSaleModalState {
  type: 'QuoteCustomer'
  onClose: (complete: IPromise<any> | undefined) => void
}

interface ServiceModalState extends BaseSaleModalState {
  type: 'Service'
}

interface UnfulfilledModalState extends BaseSaleModalState {
  type: 'Unfulfilled'
  outlets: FulfillmentOutlet[]
}

interface OpenRegisterModalState extends BaseSaleModalState {
  type: 'OpenRegister'
  onClose: (settings: CashFloatSettings | undefined) => void
}

interface OutletsDifferWarningModalState extends BaseSaleModalState {
  type: 'OutletsDifferWarning'
  outletName: string
  status: string
}

interface AddCustomerModalState extends BaseSaleModalState {
  type: 'AddCustomer'
}

interface LSPayRecoveryModalState extends BaseSaleModalState {
  type: 'LSPayRecovery'
  saleId: string
  onClose: (complete: Promise<boolean>) => void
}

type SaleModalState =
  | EmptySaleModalState
  | QuoteCustomerModalState
  | ServiceModalState
  | UnfulfilledModalState
  | OpenRegisterModalState
  | OutletsDifferWarningModalState
  | AddCustomerModalState
  | LSPayRecoveryModalState

let modalState: SaleModalState = {
  type: null,
}

let listeners: SaleModalListener[] = []

const addListener = (listener: SaleModalListener) => {
  listeners.push(listener)
}

const removeListener = (listener: SaleModalListener) => {
  listeners = listeners.filter(l => l !== listener)
}

const updateListeners = () => {
  listeners.forEach(listener => listener())
}

const subscribe = (listener: SaleModalListener) => {
  addListener(listener)
  return () => removeListener(listener)
}

const getState = () => modalState

export const useSaleModals = () => {
  const state = useSyncExternalStore<SaleModalState>(subscribe, getState)

  const closeSaleModals = useCallback(() => {
    modalState = { type: null }
    updateListeners()
    setTimeout(() => {
      $rootScope.$emit('autocomplete:global-search:focus')
    })
  }, [])

  const openQuoteCustomerModal = useCallback(
    (onClose: (complete: IPromise<any> | undefined) => void) => {
      modalState = {
        type: 'QuoteCustomer',
        onClose: (complete: IPromise<any> | undefined) => {
          onClose(complete)
          closeSaleModals()
        },
      }
      updateListeners()
    },
    [closeSaleModals]
  )

  const openServiceModal = useCallback(() => {
    modalState = { type: 'Service' }
    updateListeners()
  }, [])

  const openMarkUnfulfilledModal = useCallback(async () => {
    const outlets: FulfillmentOutlet[] = []
    if (getFeatureFromCache('fulfill_from_another_outlet')) {
      const allOutlets = await outletsStore.all()
      allOutlets.forEach(outlet => {
        outlets.push({ id: outlet.id, name: outlet.name })
      })
    }

    modalState = { type: 'Unfulfilled', outlets }
    updateListeners()
  }, [])

  const openOutletsDifferWarningModal = useCallback(
    ({ outletName, status }: { outletName: string; status: string }) => {
      modalState = { type: 'OutletsDifferWarning', outletName, status }
      updateListeners()
    },
    []
  )

  const openRegisterModal = useCallback(
    (onClose: (settings: CashFloatSettings | undefined) => void) => {
      const close = (settings: CashFloatSettings | undefined) => {
        onClose(settings)
        closeSaleModals()
      }
      modalState = { type: 'OpenRegister', onClose: close }
      updateListeners()
    },
    [closeSaleModals]
  )

  const openAddCustomerModal = useCallback(() => {
    modalState = { type: 'AddCustomer' }
    updateListeners()
  }, [])

  const openLSPayRecoveryModal = useCallback(
    (saleId: string, onClose: (complete: Promise<boolean>) => void) => {
      modalState = {
        type: 'LSPayRecovery',
        saleId,
        onClose: (complete: Promise<boolean>) => {
          onClose(complete)
          closeSaleModals()
        },
      }
      updateListeners()
    },
    [closeSaleModals]
  )

  useEffect(() => {
    return () => {
      if (listeners.length === 0) {
        closeSaleModals()
      }
    }
  }, [closeSaleModals])

  return {
    state,
    openQuoteCustomerModal,
    openServiceModal,
    openMarkUnfulfilledModal,
    openRegisterModal,
    openOutletsDifferWarningModal,
    openAddCustomerModal,
    openLSPayRecoveryModal,
    closeSaleModals,
  }
}
